import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { formatCurrency } from '../../utils/currency';
import { transactionService } from '../../services/transactionService';
import { accountService } from '../../services/accountService';
import { categoryService } from '../../services/categoryService';
import { useAuth } from '../../contexts/AuthContext';

const BillPaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const billData = location?.state?.billData;
  const accountId = location?.state?.accountId;

  const [account, setAccount] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionRef, setTransactionRef] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'expense',
    category: '',
    subcategory: '',
    memo: '',
    cleared: false,
    scheduleType: 'immediate',
    frequency: ''
  });

  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!billData || !accountId) {
      navigate('/bills-recurring');
      return;
    }
    loadInitialData();
  }, [billData, accountId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load account details
      const accountData = await accountService?.getById(accountId);
      setAccount(accountData);

      // Load categories
      const categoriesData = await categoryService?.getAll();
      setCategories(categoriesData || []);

      // Pre-fill form with bill data
      if (billData && categoriesData?.length > 0) {
        const allCats = categoriesData?.flatMap(parent => [parent, ...(parent?.subcategories || [])]);
        const selectedCat = allCats?.find(cat => cat?.id === billData?.categoryId);

        let categoryId = billData?.categoryId;
        let subcategoryId = '';
        let parent = null;

        if (selectedCat?.parentId) {
          // This is a subcategory
          categoryId = selectedCat?.parentId;
          subcategoryId = selectedCat?.id;
          parent = categoriesData?.find(cat => cat?.id === selectedCat?.parentId);
        } else {
          // This is a parent category
          parent = categoriesData?.find(cat => cat?.id === billData?.categoryId);
        }

        setSelectedParentCategory(parent);
        setAvailableSubcategories(parent?.subcategories || []);

        setFormData({
          date: billData?.date ? new Date(billData?.date)?.toISOString()?.split('T')?.[0] : new Date()?.toISOString()?.split('T')?.[0],
          description: billData?.description || '',
          amount: billData?.amount ? Math.abs(billData?.amount)?.toString() : '',
          type: billData?.type || 'expense',
          category: categoryId || '',
          subcategory: subcategoryId || '',
          memo: '',
          cleared: false,
          scheduleType: 'immediate',
          frequency: billData?.frequency || ''
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (categoryId) => {
    let parent = categories?.find(cat => cat?.id === categoryId);
    setSelectedParentCategory(parent);
    setAvailableSubcategories(parent?.subcategories || []);
    setFormData(prev => ({
      ...prev,
      category: categoryId,
      subcategory: ''
    }));
  };

  const calculateBalanceImpact = () => {
    const amount = parseFloat(formData?.amount) || 0;
    const currentBalance = account?.balance || 0;
    const isLiability = account?.group === 'Liabilities';

    let newBalance = currentBalance;

    if (formData?.type === 'expense') {
      newBalance = isLiability ? currentBalance + amount : currentBalance - amount;
    } else if (formData?.type === 'income') {
      newBalance = isLiability ? currentBalance - amount : currentBalance + amount;
    } else if (formData?.type === 'payment') {
      newBalance = isLiability ? currentBalance - amount : currentBalance + amount;
    }

    return { currentBalance, newBalance, amount };
  };

  const handleConfirmPayment = async () => {
    try {
      setSubmitting(true);
      setError('');

      // Validate form
      if (!formData?.date || !formData?.description || !formData?.amount || !formData?.category) {
        setError('Please fill in all required fields');
        return;
      }

      // Prepare transaction data
      const transactionData = {
        accountId: accountId,
        date: formData?.date,
        description: formData?.description,
        amount: parseFloat(formData?.amount),
        type: formData?.type,
        categoryId: formData?.subcategory || formData?.category,
        cleared: formData?.cleared,
        notes: formData?.memo || ''
      };

      // Create transaction - real-time subscription will automatically update bills page
      const result = await transactionService?.create(transactionData);
      
      // Generate transaction reference
      const ref = `TXN-${Date.now()?.toString()?.slice(-8)}`;
      setTransactionRef(ref);
      setShowSuccess(true);

      // Auto-redirect after 2 seconds (reduced from 3 for better UX)
      setTimeout(() => {
        navigate('/bills-recurring');
      }, 2000);
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err?.message || 'Failed to create transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/bills-recurring');
  };

  const balanceImpact = calculateBalanceImpact();

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Payment Confirmed - Rovyn</title>
        </Helmet>
        <Header />
        <div className="max-w-4xl mx-auto px-16 py-24">
          <div className="bg-card rounded-lg border border-border p-32 text-center">
            <div className="w-64 h-64 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-24">
              <Icon name="CheckCircle" size={40} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-12">Payment Confirmed!</h1>
            <p className="text-lg text-muted-foreground mb-8">Your bill payment has been successfully posted to your account.</p>
            <div className="bg-muted rounded-lg p-16 mb-24 inline-block">
              <p className="text-sm text-muted-foreground mb-4">Transaction Reference</p>
              <p className="text-xl font-mono font-semibold text-foreground">{transactionRef}</p>
            </div>
            <div className="space-y-8 mb-24">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="font-semibold text-foreground">{formatCurrency(balanceImpact?.amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Account:</span>
                <span className="font-semibold text-foreground">{account?.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">New Balance:</span>
                <span className="font-semibold text-foreground">{formatCurrency(balanceImpact?.newBalance)}</span>
              </div>
            </div>
            <Button onClick={() => navigate('/bills-recurring')} className="w-full md:w-auto">
              Return to Bills & Recurring
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Confirm Bill Payment - Rovyn</title>
      </Helmet>
      <Header />
      <div className="max-w-5xl mx-auto px-16 py-24">
        <Breadcrumbs
          items={[
            { label: 'Bills & Recurring', path: '/bills-recurring' },
            { label: 'Confirm Payment', path: '/bill-payment-confirmation' }
          ]}
        />

        <div className="mt-24">
          <div className="flex items-center gap-12 mb-24">
            <Icon name="FileText" size={32} className="text-primary" />
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">Confirm Bill Payment</h1>
              <p className="text-muted-foreground mt-4">Review and confirm your bill payment details before posting to your account</p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-16 mb-24 flex items-start gap-12">
              <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-2" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-4">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-24">
            {/* Left Column - Bill Summary */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg border border-border p-20">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-16 flex items-center gap-8">
                  <Icon name="Receipt" size={20} className="text-primary" />
                  Bill Summary
                </h2>
                <div className="space-y-12">
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Payee</p>
                    <p className="text-sm font-semibold text-foreground">{billData?.description}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Bill Amount</p>
                    <p className="text-lg font-bold text-foreground">{formatCurrency(billData?.amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Due Date</p>
                    <p className="text-sm font-semibold text-foreground">
                      {billData?.date ? new Date(billData?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Payment Account</p>
                    <p className="text-sm font-semibold text-foreground">{account?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-4">Transaction Type</p>
                    <p className="text-sm font-semibold text-foreground capitalize">{formData?.type}</p>
                  </div>
                  {formData?.frequency && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-4">Payment Schedule</p>
                      <p className="text-sm font-semibold text-foreground capitalize">{formData?.frequency}</p>
                    </div>
                  )}
                </div>

                {/* Payment Urgency Indicator */}
                {billData?.date && new Date(billData?.date) <= new Date() && (
                  <div className="mt-16 bg-red-50 border border-red-200 rounded-lg p-12 flex items-start gap-8">
                    <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0 mt-2" />
                    <div>
                      <p className="text-xs font-semibold text-red-800">Payment Overdue</p>
                      <p className="text-xs text-red-700 mt-2">This bill is past its due date</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Account Balance Impact */}
              <div className="bg-card rounded-lg border border-border p-20 mt-16">
                <h2 className="text-lg font-heading font-semibold text-foreground mb-16 flex items-center gap-8">
                  <Icon name="TrendingDown" size={20} className="text-primary" />
                  Balance Impact
                </h2>
                <div className="space-y-12">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Current Balance</span>
                    <span className="text-sm font-semibold text-foreground">{formatCurrency(balanceImpact?.currentBalance)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Payment Amount</span>
                    <span className="text-sm font-semibold text-red-600">-{formatCurrency(balanceImpact?.amount)}</span>
                  </div>
                  <div className="border-t border-border pt-12 flex justify-between items-center">
                    <span className="text-sm font-semibold text-foreground">New Balance</span>
                    <span className={`text-lg font-bold ${
                      balanceImpact?.newBalance >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(balanceImpact?.newBalance)}
                    </span>
                  </div>
                </div>

                {/* Insufficient Funds Warning */}
                {account?.group === 'Assets' && balanceImpact?.newBalance < 0 && (
                  <div className="mt-16 bg-yellow-50 border border-yellow-200 rounded-lg p-12 flex items-start gap-8">
                    <Icon name="AlertTriangle" size={16} className="text-yellow-600 flex-shrink-0 mt-2" />
                    <div>
                      <p className="text-xs font-semibold text-yellow-800">Insufficient Funds</p>
                      <p className="text-xs text-yellow-700 mt-2">This payment will result in a negative balance</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Transaction Details */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-lg border border-border p-24">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-20 flex items-center gap-8">
                  <Icon name="Edit3" size={24} className="text-primary" />
                  Transaction Details
                </h2>

                <div className="space-y-20">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Payment Date <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="date"
                      value={formData?.date}
                      onChange={(e) => handleInputChange('date', e?.target?.value)}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={formData?.description}
                      onChange={(e) => handleInputChange('description', e?.target?.value)}
                      placeholder="Enter payment description"
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData?.amount}
                      onChange={(e) => handleInputChange('amount', e?.target?.value)}
                      placeholder="0.00"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Transaction Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-8 mb-12">
                      <button
                        type="button"
                        onClick={() => handleInputChange('type', 'expense')}
                        className={`flex-1 px-16 py-10 rounded-lg border-2 transition-all ${
                          formData?.type === 'expense' ?'border-error bg-error/10 text-error font-semibold' :'border-border bg-background text-muted-foreground hover:border-error/50'
                        }`}
                      >
                        <Icon name="TrendingDown" size={16} className="inline mr-6" />
                        Expense
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInputChange('type', 'income')}
                        className={`flex-1 px-16 py-10 rounded-lg border-2 transition-all ${
                          formData?.type === 'income' ?'border-success bg-success/10 text-success font-semibold' :'border-border bg-background text-muted-foreground hover:border-success/50'
                        }`}
                      >
                        <Icon name="TrendingUp" size={16} className="inline mr-6" />
                        Income
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInputChange('type', 'payment')}
                        className={`flex-1 px-16 py-10 rounded-lg border-2 transition-all ${
                          formData?.type === 'payment' ?'border-primary bg-primary/10 text-primary font-semibold' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <Icon name="CreditCard" size={16} className="inline mr-6" />
                        Payment
                      </button>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={formData?.category}
                      onChange={(e) => handleCategoryChange(e?.target?.value)}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories?.map(cat => (
                        <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                      ))}
                    </Select>
                  </div>

                  {/* Subcategory */}
                  {availableSubcategories?.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-8">
                        Subcategory
                      </label>
                      <Select
                        value={formData?.subcategory}
                        onChange={(e) => handleInputChange('subcategory', e?.target?.value)}
                      >
                        <option value="">Select Subcategory (Optional)</option>
                        {availableSubcategories?.map(sub => (
                          <option key={sub?.id} value={sub?.id}>{sub?.name}</option>
                        ))}
                      </Select>
                    </div>
                  )}

                  {/* Memo */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Memo (Optional)
                    </label>
                    <Input
                      type="text"
                      value={formData?.memo}
                      onChange={(e) => handleInputChange('memo', e?.target?.value)}
                      placeholder="Add notes or memo"
                    />
                  </div>

                  {/* Cleared Checkbox */}
                  <div className="flex items-center gap-8">
                    <Checkbox
                      id="cleared"
                      checked={formData?.cleared}
                      onCheckedChange={(checked) => handleInputChange('cleared', checked)}
                    />
                    <label htmlFor="cleared" className="text-sm text-foreground cursor-pointer">
                      Mark as cleared
                    </label>
                  </div>

                  {/* Schedule Type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-8">
                      Payment Scheduling
                    </label>
                    <Select
                      value={formData?.scheduleType}
                      onChange={(e) => handleInputChange('scheduleType', e?.target?.value)}
                    >
                      <option value="immediate">Post Immediately</option>
                      <option value="scheduled">Schedule for Selected Date</option>
                    </Select>
                    {formData?.scheduleType === 'scheduled' && (
                      <p className="text-xs text-muted-foreground mt-8">
                        Transaction will be posted on {formData?.date ? new Date(formData?.date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'selected date'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Transaction Preview */}
                <div className="mt-24 bg-muted rounded-lg p-20">
                  <h3 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                    <Icon name="Eye" size={16} className="text-primary" />
                    Transaction Preview
                  </h3>
                  <div className="space-y-8 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="text-foreground font-medium">
                        {formData?.date ? new Date(formData?.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Description:</span>
                      <span className="text-foreground font-medium">{formData?.description || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="text-foreground font-medium">{formData?.amount ? formatCurrency(parseFloat(formData?.amount)) : '$0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Category:</span>
                      <span className="text-foreground font-medium">
                        {selectedParentCategory?.name || 'Not selected'}
                        {formData?.subcategory && availableSubcategories?.find(s => s?.id === formData?.subcategory) && (
                          <> → {availableSubcategories?.find(s => s?.id === formData?.subcategory)?.name}</>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <span className="text-foreground font-medium">{formData?.cleared ? 'Cleared' : 'Pending'}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-24 flex flex-col sm:flex-row gap-12">
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={submitting || !formData?.date || !formData?.description || !formData?.amount || !formData?.category}
                    className="flex-1"
                  >
                    {submitting ? (
                      <>
                        <Icon name="Loader" size={16} className="animate-spin mr-8" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Icon name="CheckCircle" size={16} className="mr-8" />
                        Confirm Payment
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={submitting}
                    className="flex-1"
                  >
                    <Icon name="X" size={16} className="mr-8" />
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillPaymentConfirmation;